import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  Skeleton,
  Button,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { CheckSquare, Calendar, ArrowRight } from 'lucide-react';
import dayjs from 'dayjs';

import { leaveService, reviewService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { LeaveRequest } from '@/types';

/**
 * PendingApprovalsWidget (Manager View)
 * Shows leave requests and reviews awaiting approval
 * Enables quick manager action
 */
const PendingApprovalsWidget: React.FC = () => {
  const navigate = useNavigate();

  // Fetch pending leaves from API
  const { data: pendingLeavesResponse, isLoading: leavesLoading } = useQuery({
    queryKey: [...QUERY_KEYS.pendingApprovals, 'leaves'],
    queryFn: () => leaveService.getPendingApprovals(),
    staleTime: 60000,
  });
  const pendingLeaves: LeaveRequest[] = pendingLeavesResponse?.data ?? [];

  // Fetch pending reviews
  const { data: reviewsData, isLoading: reviewsLoading } = useQuery({
    queryKey: QUERY_KEYS.myPendingReviews,
    queryFn: () => reviewService.getMyPendingReviews(),
    staleTime: 60000,
  });

  const pendingReviews: any[] = Array.isArray(reviewsData?.data)
    ? reviewsData?.data
    : (reviewsData?.data as any)?.items ?? [];
  const isLoading = leavesLoading || reviewsLoading;

  const totalPending = pendingLeaves.length + pendingReviews.length;

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width={180} height={28} />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={56} sx={{ mt: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckSquare size={20} style={{ color: '#64748b' }} />
            <Typography variant="h6" fontWeight={600}>
              Pending Approvals
            </Typography>
          </Box>
          {totalPending > 0 && (
            <Chip
              size="small"
              label={totalPending}
              color="warning"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>

        {/* Empty State */}
        {totalPending === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CheckSquare size={32} style={{ color: '#22c55e', marginBottom: 8 }} />
            <Typography variant="body2" color="text.secondary">
              No pending approvals
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ '& > *:not(:last-child)': { mb: 1 } }}>
            {/* Leave Requests */}
            {pendingLeaves.slice(0, 3).map((leave) => (
              <ListItem
                key={leave.id}
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => navigate('/reviews')}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: 'info.main',
                    mr: 1.5,
                    fontSize: '0.75rem',
                  }}
                >
                  <Calendar size={16} />
                </Avatar>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={500}>
                      Leave Request
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(leave.startDate).format('MMM D')} - {dayjs(leave.endDate).format('MMM D')}
                    </Typography>
                  }
                />
                <Chip size="small" label="Leave" color="info" sx={{ height: 20 }} />
              </ListItem>
            ))}

            {/* Reviews */}
            {pendingReviews.slice(0, 3).map((review) => (
              <ListItem
                key={review.id}
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => navigate('/reviews')}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: 'primary.main',
                    mr: 1.5,
                    fontSize: '0.75rem',
                  }}
                >
                  <CheckSquare size={16} />
                </Avatar>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={500}>
                      Task Review
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {review.type} review
                    </Typography>
                  }
                />
                <Chip size="small" label="Review" color="primary" sx={{ height: 20 }} />
              </ListItem>
            ))}
          </List>
        )}

        {/* View All Button */}
        {totalPending > 3 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              size="small"
              endIcon={<ArrowRight size={14} />}
              onClick={() => navigate('/reviews')}
            >
              View All ({totalPending})
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PendingApprovalsWidget;
